import { createApi } from '@reduxjs/toolkit/query/react';
import { HttpStatus } from '../../common/enums/HttpStatus';
import { SupportStatus } from '../../common/enums/SupportStatus';
import {
  ISupportTicket,
  ISupportTicketListResponse,
  ITicketReplyUser,
} from '../../common/interfaces/TechnicalSupport.interface';
import { createBaseQuery } from './utils/createBaseQuery';

export const istiftaApi = createApi({
  reducerPath: 'istiftaApi',
  baseQuery: createBaseQuery('/support-tickets'),
  tagTypes: ['Istifta'],
  endpoints: (builder) => ({
    getIstiftaList: builder.query<
      { code: HttpStatus; data: ISupportTicketListResponse },
      { page: number; size: number }
    >({
      query: ({ page, size }) => ({
        url: '/istifta',
        params: { page, size },
      }),
      providesTags: (result) =>
        result
          ? [
              { type: 'Istifta' as const, id: 'LIST' },
              ...result.data.items.map(({ id }) => ({
                type: 'Istifta' as const,
                id,
              })),
            ]
          : [{ type: 'Istifta' as const, id: 'LIST' }],
    }),

    getIstiftaByStatus: builder.query<
      { code: HttpStatus; data: ISupportTicketListResponse },
      { status: SupportStatus; page: number; size: number }
    >({
      query: ({ status, page, size }) => ({
        url: `/istifta/status/${status}`,
        params: { page, size },
      }),
      providesTags: (result) =>
        result
          ? [
              { type: 'Istifta' as const, id: 'LIST' },
              ...result.data.items.map(({ id }) => ({
                type: 'Istifta' as const,
                id,
              })),
            ]
          : [{ type: 'Istifta' as const, id: 'LIST' }],
    }),

    getIstiftaById: builder.query<
      { code: HttpStatus; data: ISupportTicket },
      string
    >({
      query: (ticketId) => `/${ticketId}`,
      providesTags: (_r, _e, id) => [{ type: 'Istifta', id }],
    }),

    replyToIstifta: builder.mutation<
      { code: HttpStatus; data: ISupportTicket },
      {
        ticketId: string;
        message: string;
        to?: string;
        fromUser: ITicketReplyUser;
      }
    >({
      query: ({ ticketId, message, to }) => ({
        url: `/${ticketId}/reply`,
        method: 'PUT',
        body: { message, to },
      }),
      onQueryStarted: async (
        { ticketId, message, to, fromUser },
        { dispatch, queryFulfilled }
      ) => {
        const patchResult = dispatch(
          istiftaApi.util.updateQueryData(
            'getIstiftaById',
            ticketId,
            (draft) => {
              if (!draft.data.replies) draft.data.replies = [];
              draft.data.replies.push({
                from: fromUser.id,
                to: to ?? '',
                fromUser,
                toUser: to ? { id: to } : undefined,
                message,
                sendAt: new Date().toISOString(),
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_r, _e, { ticketId }) => [
        { type: 'Istifta', id: ticketId },
        { type: 'Istifta', id: 'LIST' },
      ],
    }),

    updateIstiftaStatus: builder.mutation<
      { code: HttpStatus; data: ISupportTicket },
      { id: string; status: SupportStatus }
    >({
      query: ({ id, status }) => ({
        url: `/${id}/status`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Istifta', id },
        { type: 'Istifta', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetIstiftaListQuery,
  useGetIstiftaByStatusQuery,
  useGetIstiftaByIdQuery,
  useReplyToIstiftaMutation,
  useUpdateIstiftaStatusMutation,
} = istiftaApi;
